/**
 * Modal disparado na rota pública `/proposta/:token` quando o lead
 * clica "Tenho uma dúvida antes de aceitar" (PR-M1-01c-ii.c).
 *
 * Mensagem única — modelo 1:1 simples (não threaded). A resposta da
 * Karla vem por email/WhatsApp out-of-band — nada de conversa in-app
 * nesta versão. Ver docs/edge-functions/submit-proposal-question.md.
 *
 * UX:
 *   - Textarea grande, limite 2000 chars (matches schema backend)
 *   - Botão submit só habilita quando ≥ 10 chars (matches schema)
 *   - Estados: idle → loading → success (fecha + toast) / error (mostra reason)
 *   - Ao fechar com sucesso, invalidação no hook recarrega `usePublicProposal`
 *     e a página mostra o banner de confirmação que renderizamos em
 *     `PublicProposalPage` quando status === 'in_negotiation'.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  toast,
} from '@kmgestao/ui';

import { useSubmitProposalQuestion } from '../../features/proposals/index.js';

export interface ProposalQuestionDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  token: string;
  /** Nome do lead pra personalizar a saudação ("Olá, Dra. Danielle"). */
  leadFirstName: string;
  /** Chamado após sucesso — pai pode atualizar estado local, mostrar
   * banner, scroll pra topo etc. */
  onSubmitted?: () => void;
}

const MIN_LENGTH = 10;
const MAX_LENGTH = 2_000;

export function ProposalQuestionDialog({
  open,
  onOpenChange,
  token,
  leadFirstName,
  onSubmitted,
}: ProposalQuestionDialogProps) {
  const [message, setMessage] = useState('');
  const submitQuestion = useSubmitProposalQuestion();

  const trimmed = message.trim();
  const disabledSubmit = trimmed.length < MIN_LENGTH || submitQuestion.isPending;

  const handleSubmit = () => {
    submitQuestion.mutate(
      { token, message: trimmed },
      {
        onSuccess: () => {
          toast.success('Sua dúvida foi enviada.', {
            description: 'A equipe responderá pelo email de contato em até 1 dia útil.',
          });
          setMessage('');
          onOpenChange(false);
          onSubmitted?.();
        },
        onError: (err) =>
          toast.error('Falha ao enviar dúvida', {
            description: err.message,
          }),
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (submitQuestion.isPending) return; // não fecha enquanto processando
    onOpenChange(next);
    if (!next) {
      // Não limpa texto ao fechar manualmente — usuário pode ter clicado
      // por engano e não quer perder o que digitou. Limpeza só acontece
      // após submit bem-sucedido.
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Tem uma dúvida antes de aceitar?</DialogTitle>
          <DialogDescription>
            {leadFirstName}, escreva sua dúvida abaixo e nossa equipe responderá pelo seu email em
            até 1 dia útil. Sua mensagem não impede o aceite posterior — você pode retornar a esta
            página a qualquer momento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="proposal-question-message">Sua dúvida</Label>
          <textarea
            id="proposal-question-message"
            rows={6}
            maxLength={MAX_LENGTH}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitQuestion.isPending}
            placeholder="Ex: Poderia me explicar melhor o item X da consultoria? Tenho receio de Y…"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Mínimo {MIN_LENGTH} caracteres ·{' '}
              <span
                className={trimmed.length >= MIN_LENGTH ? 'text-accent' : 'text-muted-foreground'}
              >
                {trimmed.length}/{MAX_LENGTH}
              </span>
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg" disabled={submitQuestion.isPending}>
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="button"
            size="lg"
            onClick={handleSubmit}
            disabled={disabledSubmit}
            aria-busy={submitQuestion.isPending || undefined}
          >
            {submitQuestion.isPending ? 'Enviando…' : 'Enviar dúvida'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
